import { perspectiveTransform, applyH, type Pt, type Mat3 } from '../../utils/projection';

// Shared canvas perspective-warp used by the zoning CropPreview and the
// dashboard's client-side crops (PolygonCrop): approximates cv2.warpPerspective
// by subdividing the quad into a MESH×MESH grid and affine-drawing each cell's
// two triangles. 16×16 is visually indistinguishable from the real warp at
// preview sizes.
export const MESH = 16;

/** Affine-draw one source-image triangle into a destination triangle.
 *
 * The clip triangle is inflated ~1.6px outward from its centroid: with exact
 * triangles, antialiasing along each clip edge leaves hairline seams between
 * neighbouring mesh cells — on photos it reads as a grid ("projection mesh")
 * overlaid on the crop. Slightly overlapping clips paint the same warped
 * content twice along the borders, which hides the seams; the affine mapping
 * itself still uses the exact vertices. (0.7px proved too thin on large
 * upscaled crops — the grid ghosted back on big dark tiles.) */
export function drawTriangle(
  ctx: CanvasRenderingContext2D, img: HTMLImageElement,
  s0: Pt, s1: Pt, s2: Pt, d0: Pt, d1: Pt, d2: Pt,
) {
  const cx = (d0[0] + d1[0] + d2[0]) / 3;
  const cy = (d0[1] + d1[1] + d2[1]) / 3;
  const grow = (p: Pt): Pt => {
    const dx = p[0] - cx, dy = p[1] - cy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return p;
    const k = (len + 1.6) / len;
    return [cx + dx * k, cy + dy * k];
  };
  const g0 = grow(d0), g1 = grow(d1), g2 = grow(d2);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(g0[0], g0[1]); ctx.lineTo(g1[0], g1[1]); ctx.lineTo(g2[0], g2[1]); ctx.closePath();
  ctx.clip();
  // affine a..f mapping src -> dst
  const denom = (s1[0] - s0[0]) * (s2[1] - s0[1]) - (s2[0] - s0[0]) * (s1[1] - s0[1]);
  if (Math.abs(denom) < 1e-9) { ctx.restore(); return; }
  const a = ((d1[0] - d0[0]) * (s2[1] - s0[1]) - (d2[0] - d0[0]) * (s1[1] - s0[1])) / denom;
  const b = ((d2[0] - d0[0]) * (s1[0] - s0[0]) - (d1[0] - d0[0]) * (s2[0] - s0[0])) / denom;
  const c = ((d1[1] - d0[1]) * (s2[1] - s0[1]) - (d2[1] - d0[1]) * (s1[1] - s0[1])) / denom;
  const d = ((d2[1] - d0[1]) * (s1[0] - s0[0]) - (d1[1] - d0[1]) * (s2[0] - s0[0])) / denom;
  const e = d0[0] - a * s0[0] - b * s0[1];
  const f = d0[1] - c * s0[0] - d * s0[1];
  ctx.setTransform(a, c, b, d, e, f);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/**
 * Warp the source-image quad `srcPx` (TL,TR,BR,BL in image px) onto the
 * axis-aligned rect [offX, offY, drawW, drawH] of `ctx`. (pw, ph) define the
 * quad's perspective-rect space — only their RATIO matters for the mapping.
 */
export function drawWarpedQuad(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  srcPx: Pt[],
  pw: number,
  ph: number,
  drawW: number,
  drawH: number,
  offX = 0,
  offY = 0,
) {
  const Hrect2img: Mat3 = perspectiveTransform([[0, 0], [pw, 0], [pw, ph], [0, ph]], srcPx);
  const toDst = (rx: number, ry: number): Pt => [offX + (rx / pw) * drawW, offY + (ry / ph) * drawH];
  for (let r = 0; r < MESH; r++) {
    for (let cIdx = 0; cIdx < MESH; cIdx++) {
      const rx0 = (cIdx / MESH) * pw, rx1 = ((cIdx + 1) / MESH) * pw;
      const ry0 = (r / MESH) * ph, ry1 = ((r + 1) / MESH) * ph;
      const s00 = applyH(Hrect2img, [rx0, ry0]);
      const s10 = applyH(Hrect2img, [rx1, ry0]);
      const s11 = applyH(Hrect2img, [rx1, ry1]);
      const s01 = applyH(Hrect2img, [rx0, ry1]);
      const d00 = toDst(rx0, ry0), d10 = toDst(rx1, ry0), d11 = toDst(rx1, ry1), d01 = toDst(rx0, ry1);
      drawTriangle(ctx, img, s00, s10, s11, d00, d10, d11);
      drawTriangle(ctx, img, s00, s11, s01, d00, d11, d01);
    }
  }
}
